// ─────────────────────────────────────────────────────────────────────────────
// «صفحهٔ من» — the shared, platform-free logic of the page builder
// (docs/DOCTOR_PUBLIC_PAGE_DESIGN.md §6, §7, §12).
//
// Everything both surfaces must agree on lives here, so MyPage.web.jsx and
// MyPage.mobile.jsx cannot disagree on a derived fact: which templates,
// palettes and densities exist (the MANIFEST, never a second copy), what a
// section is called, what a reorder sends, whether «انتشار» is possible, what
// a slug may look like, and what counts as an unsaved change.
//
// No components, no `@parsina/framework` — a logic file is DOM-free.
// ─────────────────────────────────────────────────────────────────────────────

// BYTE-IDENTICAL copy of the practitioner service's
// app/domain/doctorpage_templates.json — `make doctorpage-manifests-check`.
import MANIFEST from '../../constants/doctorpage_templates.json';

export { MANIFEST };
export const LIMITS = MANIFEST.limits;

/** The closed section vocabulary, in the default template's order. */
export const SECTION_KINDS = Object.freeze(Object.keys(MANIFEST.sections));

export const PAGE_STATUSES = Object.freeze(['draft', 'published', 'unpublished', 'suspended']);

// Status → Badge variant. A derived palette owned by this screen (DESIGN_SYSTEM
// §6.1): published is the good state, unpublished waits, suspended is the one
// genuine problem.
export const STATUS_BADGE = Object.freeze({
  draft: 'outline',
  published: 'success',
  unpublished: 'warning',
  suspended: 'destructive',
});

export const statusLabelKey = (status) =>
  (PAGE_STATUSES.includes(status) ? `myPage.status.${status}` : 'myPage.status.unknown');

// ── Templates ────────────────────────────────────────────────────────────────

export const templates = () => MANIFEST.templates;

export function templateById(id) {
  return MANIFEST.templates.find((t) => t.id === id)
    || MANIFEST.templates.find((t) => t.id === MANIFEST.default_template)
    || MANIFEST.templates[0];
}

export const palettesFor = (templateId) => templateById(templateId).palettes;
export const densitiesFor = (templateId) => templateById(templateId).densities;
export const usesCover = (templateId) => !!templateById(templateId).uses_cover;
export const templateSupports = (templateId, kind) => templateById(templateId).sections.includes(kind);

/**
 * The theme the page is really in: its stored choice when the template offers
 * it, the template default otherwise. Switching template never carries a
 * palette the new template does not have (the service would refuse it).
 */
export function effectiveTheme(templateId, theme) {
  const t = templateById(templateId);
  const palette = t.palettes.some((p) => p.id === theme?.palette) ? theme.palette : t.default_palette;
  const density = t.densities.includes(theme?.density) ? theme.density : t.default_density;
  return { palette, density };
}

/** The theme to send with a template switch. */
export const themeForTemplate = (templateId, currentTheme) => effectiveTheme(templateId, currentTheme);

// ── Sections ─────────────────────────────────────────────────────────────────

export const sectionMeta = (kind) => MANIFEST.sections[kind] || { title: kind, required: false, max_items: 0 };
export const maxItems = (kind) => sectionMeta(kind).max_items || 0;

/** The heading a section renders under: the doctor's override, else the vocabulary's. */
export const sectionTitle = (section) =>
  (section?.titleOverride && section.titleOverride.trim()) || sectionMeta(section?.kind).title;

export const orderedSections = (sections) =>
  [...(sections || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

/**
 * The ReorderSections payload after moving one section by `delta` (−1 up,
 * +1 down): EVERY kind exactly once, in the new order — which is what the
 * service requires. Returns null when the move is a no-op (already at an end).
 */
export function moveSection(sections, kind, delta) {
  const kinds = orderedSections(sections).map((s) => s.kind);
  const from = kinds.indexOf(kind);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= kinds.length || delta === 0) return null;
  const next = [...kinds];
  next.splice(from, 1);
  next.splice(to, 0, kind);
  return next;
}

/** Applies an order locally (for an immediate, reversible re-render). */
export function applyOrder(sections, kinds) {
  const pos = new Map(kinds.map((k, i) => [k, i]));
  return (sections || []).map((s) => ({ ...s, sortOrder: pos.has(s.kind) ? pos.get(s.kind) : s.sortOrder }));
}

// ── Publishing ───────────────────────────────────────────────────────────────

/** Groups publish errors/blockers by the card that owns them; '' → 'page'. */
export function errorsBySection(errors) {
  const out = {};
  for (const e of errors || []) {
    const key = e?.sectionKind || 'page';
    (out[key] ||= []).push(e);
  }
  return out;
}

/**
 * Whether «انتشار» can be pressed, and the reasons it cannot. The service is
 * the authority and re-validates on publish; this only keeps a doctor from
 * pressing a button that is certain to be refused.
 */
export function publishState(page) {
  if (!page) return { canPublish: false, reasons: [] };
  if (page.status === 'suspended') return { canPublish: false, reasons: [], suspended: true };
  const reasons = page.publishBlockers || [];
  return { canPublish: reasons.length === 0, reasons };
}

export const isLive = (page) => page?.status === 'published';
export const everPublished = (page) => Number(page?.firstPublishedAtMs || 0) > 0;
export const isReadOnly = (page) => page?.status === 'suspended';

// ── Slug ─────────────────────────────────────────────────────────────────────

const PERSIAN_DIGITS = '\u06F0\u06F1\u06F2\u06F3\u06F4\u06F5\u06F6\u06F7\u06F8\u06F9';
const ARABIC_DIGITS = '\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669';

/** Persian/Arabic-Indic digits → ASCII. Storage and wire are always ASCII. */
export function toAsciiDigits(value) {
  return String(value ?? '').replace(/[\u06F0-\u06F9\u0660-\u0669]/g, (ch) => {
    const i = PERSIAN_DIGITS.indexOf(ch);
    return String(i >= 0 ? i : ARABIC_DIGITS.indexOf(ch));
  });
}

export const normalizeSlugInput = (value) => toAsciiDigits(value).trim().toLowerCase().replace(/\s+/g, '-');

// The service's rule (app/domain/doctor_page.py `slug_format_ok`): starts and
// ends with a letter or digit, and never two hyphens in a row.
export const SLUG_PATTERN = new RegExp(
  `^[a-z0-9][a-z0-9-]{${Math.max(LIMITS.slug_min - 2, 0)},${LIMITS.slug_max - 2}}[a-z0-9]$`,
);

/** The local format check — '' when fine, else a message key. */
export function slugFormatError(slug) {
  if (!slug) return 'myPage.slug.required';
  if (!SLUG_PATTERN.test(slug) || slug.includes('--')) return 'myPage.slug.invalid';
  return '';
}

/** The slug field's mode: locked after the first publish, editable before it. */
export const slugLocked = (page) => !!page?.slugLocked;

// ── Items (services, testimonials, video) ────────────────────────────────────

// A services item id is also part of a storage owner id (`<sub>-page-svc-<id>`),
// so it must stay inside [A-Za-z0-9_-]{1,64}.
export function newItemId(random = Math.random) {
  let s = '';
  while (s.length < 12) s += Math.floor(random() * 36).toString(36);
  return `i${s.slice(0, 12)}`;
}

export const itemsOf = (section) => (Array.isArray(section?.content?.items) ? section.content.items : []);

export function canAddItem(kind, items) {
  const max = maxItems(kind);
  return !max || (items || []).length < max;
}

/** Media for one service item (slot 'service', ref_id = item id). */
export const serviceImage = (page, itemId) =>
  (page?.media || []).find((m) => m.slot === 'service' && m.refId === itemId) || null;

export const galleryMedia = (page) =>
  (page?.media || []).filter((m) => m.slot === 'gallery').sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

// ── Hero extras (cards beside the portrait, highlights under the hero) ───────
// Two short lists in the hero section's content. Which templates DRAW them is
// the renderer's business (the two landing-style ones, «مدرن» and «کلاسیک»);
// the editor only says so.

export const HERO_EXTRAS_TEMPLATES = Object.freeze(['modern', 'classic']);
export const showsHeroExtras = (templateId) => HERO_EXTRAS_TEMPLATES.includes(templateById(templateId).id);

export const HERO_LISTS = Object.freeze({
  cards: { max: LIMITS.hero_cards_max, titleMax: LIMITS.hero_card_title_max, textMax: LIMITS.hero_card_text_max },
  highlights: { max: LIMITS.hero_highlights_max, titleMax: LIMITS.hero_highlight_title_max, textMax: LIMITS.hero_highlight_text_max },
});

const heroList = (list) => (Array.isArray(list) ? list : [])
  .map((it) => ({ id: it?.id || '', title: it?.title || '', text: it?.text || '' }));

/** The hero section's content in one predictable shape, whatever the service sent. */
export const heroExtrasDraft = (section) => ({
  cards: heroList(section?.content?.cards),
  highlights: heroList(section?.content?.highlights),
});

/** Draft → the content_json the service validates (trimmed; an item with no id gets one there). */
export function heroExtrasPayload(draft) {
  const clean = (list) => (list || []).map((it) => ({
    ...(it.id ? { id: it.id } : {}), title: it.title.trim(), text: (it.text || '').trim(),
  }));
  return { cards: clean(draft.cards), highlights: clean(draft.highlights) };
}

/** Whether the draft can be saved: every item titled, nothing over its limit. */
export function heroExtrasInvalid(draft) {
  return Object.entries(HERO_LISTS).some(([key, lim]) => {
    const list = draft[key] || [];
    return list.length > lim.max
      || list.some((it) => !it.title.trim() || overLimit(it.title, lim.titleMax) || overLimit(it.text, lim.textMax));
  });
}

// ── Contact / office ─────────────────────────────────────────────────────────

export const SOCIAL_KEYS = Object.freeze(['instagram', 'telegram', 'whatsapp', 'eitaa', 'bale', 'website']);

/** The office block in one predictable shape, whatever the service sent. */
export function contactDraft(contact) {
  const c = contact || {};
  const socials = {};
  for (const k of SOCIAL_KEYS) socials[k] = c.socials?.[k] || '';
  return {
    phones: (Array.isArray(c.phones) ? c.phones : []).map((p) => ({ label: p?.label || '', number: p?.number || '' })),
    address: c.address || '',
    city: c.city || '',
    hours: (Array.isArray(c.hours) ? c.hours : []).map((h) => ({ label: h?.label || '', value: h?.value || '' })),
    geo: c.geo && c.geo.lat != null && c.geo.lng != null
      ? { lat: String(c.geo.lat), lng: String(c.geo.lng) }
      : { lat: '', lng: '' },
    socials,
  };
}

/**
 * A typed coordinate → a number (NaN when there is none). A coordinate has no
 * thousands, so a comma — the French and German decimal mark — or the Persian «٫» is the
 * decimal point: '35,6892' is 35.6892, never 35.
 */
const parseCoordinate = (value) => Number.parseFloat(toAsciiDigits(value).replace(/[,\u066B]/g, '.'));

/** Draft → the contact_json the service validates (empty rows dropped, geo parsed or null). */
export function contactPayload(draft) {
  const lat = parseCoordinate(draft.geo?.lat);
  const lng = parseCoordinate(draft.geo?.lng);
  const socials = {};
  for (const k of SOCIAL_KEYS) {
    const v = (draft.socials?.[k] || '').trim();
    if (v) socials[k] = v;
  }
  return {
    phones: (draft.phones || [])
      .map((p) => ({ label: p.label.trim(), number: toAsciiDigits(p.number).trim() }))
      .filter((p) => p.number),
    address: (draft.address || '').trim(),
    city: (draft.city || '').trim(),
    geo: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null,
    hours: (draft.hours || []).map((h) => ({ label: h.label.trim(), value: h.value.trim() })).filter((h) => h.label || h.value),
    socials,
  };
}

/** The draft's geo strings → a {lat, lng} point for the map, or null when there is none. */
export function draftGeoPoint(geo) {
  const lat = parseCoordinate(geo?.lat);
  const lng = parseCoordinate(geo?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** A point picked on the map (or null) → the draft's geo strings. Six decimals ≈ 10 cm. */
export function geoDraftOf(point) {
  if (!point) return { lat: '', lng: '' };
  return { lat: Number(point.lat).toFixed(6), lng: Number(point.lng).toFixed(6) };
}

// ── Drafts ───────────────────────────────────────────────────────────────────

/** Structural equality for drafts (plain JSON data only). */
export const sameDraft = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const heroDraft = (page) => ({
  displayName: page?.displayName || '',
  headline: page?.headline || '',
  aboutMd: page?.aboutMd || '',
});

/** A card's save lifecycle, from the four facts a card knows. */
export function saveStatus({ dirty, saving, error, savedAt }) {
  if (saving) return 'saving';
  if (error) return 'error';
  if (dirty) return 'dirty';
  if (savedAt) return 'saved';
  return 'idle';
}

// ── Leads ────────────────────────────────────────────────────────────────────

export const LEAD_CHANNELS = Object.freeze(['call', 'sms', 'whatsapp']);
export const LEAD_TIMES = Object.freeze(['morning', 'afternoon', 'evening', 'any']);
export const LEADS_PAGE_SIZE = 20;

// ── URL contract ─────────────────────────────────────────────────────────────
//   /my-page?tab=builder|leads&section=<kind>
// An unknown value falls back to the first valid one, never blank.

export const TABS = Object.freeze(['builder', 'leads']);
export const tabFromParam = (value) => (TABS.includes(value) ? value : 'builder');
export const sectionFromParam = (value) => (SECTION_KINDS.includes(value) ? value : null);

// ── Markdown ─────────────────────────────────────────────────────────────────

/** Characters, for the «n / max» counters. */
export const charCount = (text) => [...String(text || '')].length;
export const overLimit = (text, max) => !!max && charCount(text) > max;

// ── Booking («نوبت‌دهی», PM-0468 §2.5 / §4.5) ────────────────────────────────
// The section's content is `{ clinic_id, services: [catalog_id], mode,
// horizon_days }`. The doctor picks the clinic, narrows the list and chooses
// slots vs. request; whether a service is public or directly bookable is the
// CLINIC's flag, read from `myPageBookingOptions` and never written here.

export const BOOKING_MODES = Object.freeze(['slots', 'request']);

export const BOOKING_LIMITS = Object.freeze({
  servicesMax: LIMITS.booking_services_max,
  horizonMin: LIMITS.booking_horizon_min,
  horizonMax: LIMITS.booking_horizon_max,
  horizonDefault: LIMITS.booking_horizon_default,
});

const clampHorizon = (value) => {
  const n = Number.parseInt(toAsciiDigits(value), 10);
  if (!Number.isFinite(n)) return BOOKING_LIMITS.horizonDefault;
  return Math.min(BOOKING_LIMITS.horizonMax, Math.max(BOOKING_LIMITS.horizonMin, n));
};

/** The horizon picker's choices: weekly steps inside the manifest's range, the default always among them. */
export function bookingHorizonOptions() {
  const { horizonMin: min, horizonMax: max, horizonDefault: def } = BOOKING_LIMITS;
  const set = new Set([min, def, max]);
  for (let d = 7; d <= max; d += 7) if (d >= min) set.add(d);
  return [...set].sort((a, b) => a - b);
}

export const bookingClinics = (options) => (Array.isArray(options?.clinics) ? options.clinics : []);

/** The services the editor may offer for a clinic: the clinic's PUBLIC ones, in the clinic's order. */
export function bookingServicesFor(options, clinicId) {
  if (!clinicId) return [];
  const row = (options?.servicesByClinic || []).find((x) => String(x.clinicId) === String(clinicId));
  return (row?.services || []).filter((s) => s.publicVisible);
}

/** What is pre-checked on first enable or on a clinic change: the directly bookable ones. */
export const bookingDefaultServices = (options, clinicId) =>
  bookingServicesFor(options, clinicId).filter((s) => s.patientBookable).map((s) => String(s.catalogId))
    .slice(0, BOOKING_LIMITS.servicesMax);

/** Whether the section was ever saved (a stored list, even an empty one, is respected). */
export const bookingSaved = (section) => Array.isArray(section?.content?.services);

/** The section's content exactly as stored — the baseline «dirty» is measured against. */
export const bookingStored = (section) => bookingDraft(section, null, { defaults: false });

/**
 * The section's content in one predictable shape; a never-saved section starts
 * on the defaults (the sole clinic, its directly bookable services) unless
 * `defaults: false`.
 */
export function bookingDraft(section, options, { defaults = true } = {}) {
  const c = section?.content || {};
  const clinics = bookingClinics(options);
  let clinicId = c.clinic_id ? String(c.clinic_id) : '';
  if (defaults && !clinicId && clinics.length === 1) clinicId = String(clinics[0].id);
  const services = Array.isArray(c.services)
    ? c.services.map(String)
    : (defaults && clinicId && options ? bookingDefaultServices(options, clinicId) : []);
  return {
    clinicId,
    services,
    mode: BOOKING_MODES.includes(c.mode) ? c.mode : 'slots',
    horizonDays: clampHorizon(c.horizon_days),
  };
}

/** Chosen services the clinic no longer shows — struck through in the editor, dropped on save. */
export function bookingMissingServices(draft, options) {
  if (!options) return [];
  const offered = new Set(bookingServicesFor(options, draft.clinicId).map((s) => String(s.catalogId)));
  return (draft.services || []).filter((id) => !offered.has(String(id)));
}

/** Draft → the content_json the service validates. With no options loaded nothing is dropped. */
export function bookingPayload(draft, options) {
  const missing = new Set(bookingMissingServices(draft, options));
  return {
    clinic_id: draft.clinicId || '',
    services: (draft.services || []).filter((id) => !missing.has(id)),
    mode: draft.mode,
    horizon_days: draft.horizonDays,
  };
}

export const bookingInvalid = (draft) =>
  !BOOKING_MODES.includes(draft?.mode) || (draft?.services || []).length > BOOKING_LIMITS.servicesMax;

// The four publish/save codes this card owns, and the field each belongs on
// (DESIGN_CONCEPT §5.3: a red helper line under the field, never a toast).
export const BOOKING_ERROR_FIELDS = Object.freeze({
  BOOKING_CLINIC_REQUIRED: 'clinic',
  BOOKING_CLINIC_NOT_MEMBER: 'clinic',
  BOOKING_SERVICES_REQUIRED: 'services',
  BOOKING_SERVICE_UNAVAILABLE: 'services',
});

export const bookingFieldOf = (code) => BOOKING_ERROR_FIELDS[code] || null;

/** Splits a list of publish errors into the ones a booking field shows and the rest. */
export function splitBookingFieldErrors(errors) {
  const fields = { clinic: [], services: [] };
  const rest = [];
  for (const e of errors || []) {
    const field = bookingFieldOf(e?.code);
    if (field) fields[field].push(e); else rest.push(e);
  }
  return { fields, rest };
}
